/**
 * useShortcutGuard — §8.12 / §31
 *
 * Returns the current shortcut context so handlers can decide
 * whether to act on a key event. Also provides a recently-blurred guard
 * that prevents shortcuts from firing within 150ms of leaving a text input
 * (prevents phantom keystrokes from being interpreted as shortcuts).
 *
 * Usage:
 *   const guard = useShortcutGuard()
 *   if (guard.context === 'text-input') return  // user is typing
 *   if (guard.recentlyBlurred) return           // just left a field
 */

import { useCallback, useEffect, useRef } from "react"
import { isTextInput } from "@/shared/utils/dom"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type ShortcutContext =
  | "text-input" // Focus inside input/textarea/contenteditable
  | "task-detail" // Task detail panel is open but no text field focused
  | "modal-open" // A modal/dialog/popover is open
  | "view-level" // Normal view — full shortcuts available

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

/** Time (ms) after a text input blur during which shortcuts are suppressed */
const BLUR_GUARD_MS = 150

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useShortcutGuard(): {
  /** Current shortcut context */
  getContext: () => ShortcutContext
  /** True if a text input was blurred less than BLUR_GUARD_MS ago */
  isRecentlyBlurred: () => boolean
  /** Combined guard: returns true if shortcuts should be suppressed */
  shouldSuppress: () => boolean
} {
  const lastBlurRef = useRef(0)

  // Track blur events from text inputs
  useEffect(() => {
    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target
      if (target instanceof Element && isTextInput(target)) {
        lastBlurRef.current = Date.now()
      }
    }
    document.addEventListener("focusout", handleFocusOut, true)
    return () => document.removeEventListener("focusout", handleFocusOut, true)
  }, [])

  const isRecentlyBlurred = useCallback(() => {
    return Date.now() - lastBlurRef.current < BLUR_GUARD_MS
  }, [])

  const getContext = useCallback((): ShortcutContext => {
    const active = document.activeElement

    // 1. Check if a text input is focused
    if (active && isTextInput(active)) {
      return "text-input"
    }

    // 2. Check for open modals/dialogs (Radix portals, shadcn dialogs)
    const hasOpenModal =
      document.querySelector("[data-state='open'][role='dialog']") !== null ||
      document.querySelector("[data-state='open'][role='alertdialog']") !== null
    if (hasOpenModal) {
      return "modal-open"
    }

    // 3. Check if task detail is open (URL has ?tid= param)
    const params = new URLSearchParams(window.location.search)
    if (params.has("tid")) {
      return "task-detail"
    }

    return "view-level"
  }, [])

  const shouldSuppress = useCallback(() => {
    const ctx = getContext()
    return ctx === "text-input" || isRecentlyBlurred()
  }, [getContext, isRecentlyBlurred])

  return { getContext, isRecentlyBlurred, shouldSuppress }
}
